import { useState, useEffect } from "react";
import axios from "axios";
import { 
  DollarSign, 
  ShoppingCart, 
  Store, 
  TrendingUp,
  Loader2,
  Percent,
  Copy
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DistributorOverview({ distributor }) {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    if (distributor) {
      fetchData();
    }
  }, [distributor]);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/distributor/stats`, getAuthHeaders()),
        axios.get(`${API}/distributor/orders?limit=5`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.orders || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      label: "Total Earnings",
      value: `$${(stats?.total_earnings || 0).toFixed(2)}`,
      icon: DollarSign,
      color: "green",
      subtext: "All time"
    },
    {
      label: "Pending Commission",
      value: `$${(stats?.pending_earnings || 0).toFixed(2)}`,
      icon: TrendingUp,
      color: "amber",
      subtext: "Awaiting payout"
    },
    {
      label: "Total Orders",
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: "blue",
      subtext: "From your resellers"
    },
    {
      label: "Assigned Resellers",
      value: stats?.assigned_resellers || 0,
      icon: Store,
      color: "purple",
      subtext: "Active stores"
    }
  ];

  return (
    <div className="space-y-6" data-testid="distributor-overview">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Welcome, {distributor?.name}!
        </h1>
        <p className="text-slate-500 flex items-center gap-2">
          Your commission rate: 
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
            <Percent className="w-3 h-3" />
            {distributor?.commission_percent || 10}%
          </span>
        </p>
      </div>

      {/*
        * Referral code.
        *
        * This is the distributor's half of the signup gate: a funeral home cannot
        * create an Infinity Touch account without quoting a code, and quoting this
        * one is what links them to this distributor. Given prominence because the
        * distributor has to hand it out for any of it to work.
        */}
      {distributor?.referral_code && (
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
            Your referral code
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <code className="font-mono text-2xl font-bold tracking-widest text-slate-900 bg-slate-100 px-4 py-2 rounded-lg">
              {distributor.referral_code}
            </code>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(distributor.referral_code);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch {
                  /* Clipboard unavailable (insecure origin, or denied) — the code
                     is on screen to copy by hand, so this needs no error state. */
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg hover:bg-slate-50"
            >
              <Copy className="w-4 h-4" />
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            Give this code to funeral homes you refer. They enter it when they sign up
            on the website or in the Infinity Touch app, and their account is credited
            to you automatically. The same code works for every funeral home.
          </p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl border p-5 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-xs text-slate-400 mt-1">{stat.subtext}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                stat.color === 'green' ? 'bg-green-100' :
                stat.color === 'amber' ? 'bg-amber-100' :
                stat.color === 'blue' ? 'bg-blue-100' : 'bg-purple-100'
              }`}>
                <stat.icon className={`w-6 h-6 ${
                  stat.color === 'green' ? 'text-green-600' :
                  stat.color === 'amber' ? 'text-amber-600' :
                  stat.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                }`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-slate-900">Recent Orders</h2>
          <p className="text-sm text-slate-500">Orders from your assigned resellers</p>
        </div>
        
        {recentOrders.length === 0 ? (
          <div className="p-8 text-center">
            <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No orders yet</p>
            <p className="text-sm text-slate-400">Orders from your resellers will appear here</p>
          </div>
        ) : (
          <div className="divide-y">
            {recentOrders.map((order) => (
              <div key={order.order_id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Order #{order.order_id.slice(-8)}</p>
                  <p className="text-sm text-slate-500">{order.store_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-green-600">
                    +${(order.distributor_commission || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-400">
                    {new Date(order.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
